// src/pages/billing/ViewPurchaseInvoicePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getPurchaseInvoiceById, deletePurchaseInvoice } from '../../services/purchaseInvoiceService';
import { printPurchaseInvoice } from '../../utils/printUtils';

const ViewPurchaseInvoicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Load invoice data
  useEffect(() => {
    const loadInvoice = async () => {
      try {
        setLoading(true);
        const invoiceData = await getPurchaseInvoiceById(id);

        // Verify this invoice belongs to the current vendor
        if (invoiceData.vendorId !== vendorProfile?.uid) {
          throw new Error('Invoice not found or access denied');
        }

        setInvoice(invoiceData);
        setError('');
      } catch (error) {
        console.error('Error loading purchase invoice:', error);
        setError('Failed to load purchase invoice. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id && vendorProfile?.uid) {
      loadInvoice();
    }
  }, [id, vendorProfile?.uid]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    // Handle different date formats
    let date;
    if (dateString.toDate) {
      // Firestore Timestamp
      date = dateString.toDate();
    } else if (dateString.seconds) {
      // Firestore Timestamp object
      date = new Date(dateString.seconds * 1000);
    } else {
      // Regular date string
      date = new Date(dateString);
    }

    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get payment terms display
  const getPaymentTermsDisplay = (terms) => {
    const displayTerms = {
      immediate: 'Paid',
      net_15: 'Net 15 days',
      net_30: 'Net 30 days',
      net_60: 'Net 60 days'
    };
    return displayTerms[terms] || terms;
  };

  // Helper function to calculate CGST and SGST
  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  // Get GST breakdown by rate for display
  const getGstBreakdown = () => {
    if (!invoice?.items || invoice.gstType !== 'with_gst') return {};

    const gstBreakdown = {};

    invoice.items.forEach(item => {
      const rate = item.gstRate || 0;
      const itemAmount = parseFloat(item.amount) || 0;
      const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemAmount, rate);

      if (rate > 0) {
        if (!gstBreakdown[rate]) {
          gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
        }
        gstBreakdown[rate].taxableAmount += itemAmount;
        gstBreakdown[rate].cgstAmount += cgstAmount;
        gstBreakdown[rate].sgstAmount += sgstAmount;
        gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
      }
    });
    return gstBreakdown;
  };

  // Handle invoice deletion
  const handleDelete = async () => {
    try {
      await deletePurchaseInvoice(id);
      showAlert('Purchase invoice deleted successfully', 'success');
      navigate('/billing?tab=purchases');
    } catch (error) {
      console.error('Error deleting purchase invoice:', error);
      showAlert('Failed to delete purchase invoice', 'error');
      setShowDeleteModal(false);
    }
  };

  // Print invoice
  const handlePrint = () => {
    const success = printPurchaseInvoice(invoice, vendorProfile);
    if (!success) {
      showAlert('Please allow popups to print invoices', 'error');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
              <span className="ml-3 text-gray-600">Loading purchase invoice...</span>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !invoice) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error || 'Purchase invoice not found'}</h3>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Button onClick={() => navigate('/billing?tab=purchases')}>
                Back to Purchase Invoices
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const gstBreakdown = getGstBreakdown();

  return (
    <DashboardLayout>
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .print-content {
            padding: 20px;
            font-size: 12px;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Actions - No Print */}
        <div className="py-6 no-print">
          <div className="flex justify-between items-start">
            <div>
              <nav className="flex mb-4" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-4">
                  <li>
                    <Link to="/billing?tab=purchases" className="text-gray-400 hover:text-gray-500">
                      <span>Purchase Invoices</span>
                    </Link>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <svg className="flex-shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                      </svg>
                      <span className="ml-4 text-sm font-medium text-gray-500">Invoice Details</span>
                    </div>
                  </li>
                </ol>
              </nav>
              <h1 className="text-2xl font-bold text-gray-900">Purchase Invoice {invoice.invoiceNumber}</h1>
              <p className="mt-1 text-sm text-gray-500">
                Supplier: {invoice.supplierName}
              </p>
            </div>
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={handlePrint}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                </svg>
                Print
              </Button>
              <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Invoice Content - Print Ready */}
        <div className="print-content bg-white border border-gray-200 rounded-lg p-8 mb-8">
          {/* Invoice Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">PURCHASE INVOICE</h1>
              <div className="text-sm text-gray-600">
                <p><strong>Invoice #:</strong> {invoice.invoiceNumber}</p>
                <p><strong>Purchase Date:</strong> {formatDate(invoice.purchaseDate)}</p>
                <p><strong>Payment Terms:</strong> {getPaymentTermsDisplay(invoice.paymentTerms)}</p>
              </div>
            </div>

            {/* Vendor Details (Buyer) */}
            <div className="text-right">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {vendorProfile?.shopName || 'Your Shop Name'}
              </h2>
              <div className="text-sm text-gray-600">
                <p>{vendorProfile?.address || 'Shop Address'}</p>
                <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                <p>Email: {vendorProfile?.email || 'N/A'}</p>
                {vendorProfile?.gstNumber && (
                  <p>GST: {vendorProfile.gstNumber}</p>
                )}
              </div>
            </div>
          </div>

          {/* Status and Type Badges - No Print */}
          <div className="mb-6 no-print">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-500">Status:</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${invoice.status === 'received' ? 'bg-green-100 text-green-800' :
                  invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    invoice.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                }`}>
                {invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : 'Unknown'}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${invoice.gstType === 'with_gst'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
                }`}>
                {invoice.gstType === 'with_gst' ? 'GST Invoice' : 'Non-GST Invoice'}
              </span>
            </div>
          </div>

          {/* Supplier Details */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Supplier Details:</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="text-sm">
                <p className="font-medium text-gray-900">{invoice.supplierName}</p>
                {invoice.supplierContact && <p className="text-gray-600">{invoice.supplierContact}</p>}
                {invoice.supplierEmail && <p className="text-gray-600">{invoice.supplierEmail}</p>}
                {invoice.supplierGST && <p className="text-gray-600">GST: {invoice.supplierGST}</p>}
                {invoice.supplierAddress && <p className="text-gray-600 mt-2 whitespace-pre-wrap">{invoice.supplierAddress}</p>}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Purchase Items</h4>
            <div className="overflow-x-auto">


              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Description
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      HSN
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Qty
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Rate (₹)
                    </th>
                    {invoice.gstType === 'with_gst' && (
                      <>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                          GST (%)
                        </th>
                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                          CGST (₹)
                        </th>
                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                          SGST (₹)
                        </th>
                      </>
                    )}
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Amount (₹)
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total (₹)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoice.items?.map((item, index) => {
                    const itemAmount = parseFloat(item.amount) || 0;
                    const { cgstAmount, sgstAmount } = calculateCGSTSGST(itemAmount, item.gstRate || 0);

                    return (
                      <tr key={index}>
                        <td className="px-3 py-4 border-r">
                          <div className="text-sm font-medium text-gray-900">{item.productName}</div>
                          {item.productId && (
                            <div className="text-xs text-gray-500 no-print">
                              <Link
                                to={`/inventory/view/${item.productId}`}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                View in Inventory →
                              </Link>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-xs text-gray-900">{item.hsnCode || 'N/A'}</span>
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-sm text-gray-900">{item.quantity}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-sm text-gray-900">{formatCurrency(item.rate)}</span>
                        </td>
                        {invoice.gstType === 'with_gst' && (
                          <>
                            <td className="px-2 py-4 text-center border-r">
                              <span className="text-sm text-gray-900">{item.gstRate || 0}%</span>
                            </td>
                            <td className="px-2 py-4 text-right border-r">
                              <span className="text-xs text-gray-900">{formatCurrency(cgstAmount)}</span>
                            </td>
                            <td className="px-2 py-4 text-right border-r">
                              <span className="text-xs text-gray-900">{formatCurrency(sgstAmount)}</span>
                            </td>
                          </>
                        )}
                        <td className="px-3 py-4 text-right border-r">
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                        </td>
                        <td className="px-3 py-4 text-right">
                          <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(itemAmount + cgstAmount + sgstAmount)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end mb-8">
            <div className="w-96">
              <div className="bg-gray-50 p-6 rounded-md border">
                <div className="space-y-2">
                  <div className="flex justify-between py-1">
                    <span className="text-sm text-gray-600">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.subtotal)}</span>
                  </div>

                  {/* GST Breakdown */}
                  {invoice.gstType === 'with_gst' && Object.keys(gstBreakdown).length > 0 && (
                    <div className="border-t border-gray-200 pt-2">
                      <div className="text-xs font-medium text-gray-700 mb-1">GST Breakdown:</div>
                      {Object.entries(gstBreakdown).map(([rate, data]) => (
                        <div key={rate} className="space-y-1 mb-2">
                          <div className="flex justify-between py-1">
                            <span className="text-xs text-gray-600">CGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.cgstAmount)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-xs text-gray-600">SGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.sgstAmount)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                        <span className="text-sm text-gray-600">Total GST:</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.totalGstAmount)}</span>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-gray-900">{formatCurrency(invoice.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mb-8">
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Notes:</h5>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}

          {/* Timestamps - No Print */}
          <div className="mb-8 no-print">
            <div className="bg-gray-50 p-4 rounded-md">
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Invoice Information:</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Created: </span>
                  <span className="text-gray-900">{formatDate(invoice.createdAt?.toDate?.() || invoice.createdAt)}</span>
                </div>
                {invoice.updatedAt && (
                  <div>
                    <span className="text-gray-600">Updated: </span>
                    <span className="text-gray-900">{formatDate(invoice.updatedAt?.toDate?.() || invoice.updatedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6">
            <div className="text-center text-sm text-gray-500">
              <p>This is a computer generated purchase invoice.</p>
              <p className="mt-2">Received by: {vendorProfile?.shopName || 'Your Shop Name'}</p>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed z-10 inset-0 overflow-y-auto no-print">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Purchase Invoice</h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete purchase invoice "{invoice.invoiceNumber}"? This action cannot be undone and may affect your inventory if products were added to stock.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ViewPurchaseInvoicePage;